import React from "react";
import { FormattedMessage, useIntl } from "react-intl";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  List,
  ListItem,
  ListItemText,
  Avatar,
  Box,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { Attendee, Schedule } from "../../../redux/types/types";
// import SpecialPackagesList from "../../Attendees/SpecialPackagesList";
import { getActiveAttendees } from "../MySchedules";
import { getAttendeeAccessDisplay } from "../../../utils/specialClassAccess";
import { RootState } from "../../../redux/store";
import { useSelector } from "react-redux";
import AttendeeImportantInfoButton from "../../Attendees/AttendeeImportantInfoButton";

interface EventDialogProps {
  isOpen: boolean;
  onClose: () => void;
  event: {
    id: string;
    title: string;
    start: Date;
    end: Date;
    extendedProps: {
      location: string;
      type: string;
      attendees: Attendee[];
      schedule: Schedule;
    };
  } | null;
}

export default function EventDialogNonInstructor({
  isOpen,
  onClose,
  event,
}: EventDialogProps) {
  const theme = useTheme();
  const intl = useIntl();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isAdmin = useSelector((state: RootState) => state.auth.isAdmin);
  const userClasses = useSelector(
    (state: RootState) => state.auth.user.classes || [],
  );
  const adminClasses = useSelector(
    (state: RootState) => state.admin.classes || [],
  );

  if (event?.extendedProps.schedule.date) {
    event.extendedProps.attendees = event?.extendedProps.attendees.filter(
      (a) => !a.unenrollDate,
    );
  }

  const generateAttendeeColor = (id: number): string => {
    const hue = (id * 137.508) % 360;
    return theme.palette.mode === "dark"
      ? `hsl(${hue}, 100%, 25%)`
      : `hsl(${hue}, 100%, 75%)`;
  };

  if (!event) return null;

  const weekdays = [
    intl.formatMessage({ id: "admin.dashboard.sunday" }),
    intl.formatMessage({ id: "admin.dashboard.monday" }),
    intl.formatMessage({ id: "admin.dashboard.tuesday" }),
    intl.formatMessage({ id: "admin.dashboard.wednesday" }),
    intl.formatMessage({ id: "admin.dashboard.thursday" }),
    intl.formatMessage({ id: "admin.dashboard.friday" }),
    intl.formatMessage({ id: "admin.dashboard.saturday" }),
  ];

  const formatTime = (date: Date) => {
    return intl.formatTime(date, {
      hour: "numeric",
      minute: "numeric",
      hour12: false,
    });
  };

  if (!event.extendedProps.schedule.date) {
    event.extendedProps.attendees = getActiveAttendees(
      event.start,
      event.extendedProps.schedule,
    );
  }

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle sx={{ pr: 6 }}>
        {event.title}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body1">
          <FormattedMessage id="mySchedules.date" defaultMessage="Date" />:{" "}
          {intl.formatDate(event.start, {
            year: "numeric",
            month: "long",
            day: "numeric",
          })}
        </Typography>
        <Typography variant="body1">
          <FormattedMessage id="mySchedules.weekday" defaultMessage="Weekday" />
          : {weekdays[event.start.getDay()]}
        </Typography>
        <Typography variant="body1">
          <FormattedMessage id="mySchedules.time" defaultMessage="Time" />:{" "}
          {formatTime(event.start)} - {formatTime(event.end)}
        </Typography>
        <Typography variant="body1">
          <FormattedMessage
            id="mySchedules.location"
            defaultMessage="Location"
          />
          : {event.extendedProps.location}
        </Typography>
        <Typography variant="body1" sx={{ mb: 2 }}>
          <FormattedMessage id="mySchedules.type" defaultMessage="Type" />:{" "}
          {event.extendedProps.type}
        </Typography>
        {Boolean(
          event?.start &&
            Boolean(
              event.extendedProps.schedule.CancelledSchedule?.find(
                (cs) =>
                  new Date(event?.start).setHours(0, 0, 0, 0) ===
                  new Date(cs.date).setHours(0, 0, 0, 0),
              ),
            ),
        ) ? (
          <>
            <Typography variant="h3" component="div" color={"red"}>
              {intl.formatMessage({
                id: "upcomingClasses.Cancelled",
                defaultMessage: "Cancelled",
              })}
            </Typography>
            <Typography variant="h6" component="div">
              {
                event.extendedProps.schedule.CancelledSchedule?.find(
                  (cs) =>
                    new Date(event?.start).setHours(0, 0, 0, 0) ===
                    new Date(cs.date).setHours(0, 0, 0, 0),
                )?.comment
              }
            </Typography>
          </>
        ) : (
          <>
            <Typography variant="h6" sx={{ mb: 1 }}>
              <FormattedMessage
                id="mySchedules.attendees"
                defaultMessage="Attendees"
              />
              :
            </Typography>
            <List>
              {event.extendedProps.attendees.map((attendee: Attendee) => (
                <ListItem key={attendee.id} sx={{ alignItems: "flex-start" }}>
                  <Avatar
                    sx={{
                      bgcolor: generateAttendeeColor(attendee.id),
                      mr: 2,
                    }}
                  >
                    {attendee.firstName[0]}
                  </Avatar>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <ListItemText
                      sx={{ m: 0 }}
                      primary={`${attendee.firstName} ${attendee.lastName}`}
                      secondary={
                        <>
                          <Typography component="span" variant="body2">
                            {attendee.email}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            <FormattedMessage id="attendee.permisStatus.title" />
                            :{" "}
                            {(attendee.permisStatus === "PermisS" &&
                              intl.formatMessage({
                                id: "attendee.permisStatus.permisS",
                              })) ||
                              (attendee.permisStatus === "Other" &&
                                intl.formatMessage({
                                  id: "attendee.permisStatus.other",
                                }))}
                          </Typography>
                          {(() => {
                            const classesData = isAdmin
                              ? adminClasses
                              : userClasses;
                            const currentClass = classesData.find(
                              (cls: any) =>
                                cls.id === event.extendedProps.schedule.classId,
                            );
                            // Show for Other status or if class is special/requiresPayment
                            if (
                              attendee.permisStatus === "Other" ||
                              currentClass?.isSpecial ||
                              currentClass?.requiresPayment
                            ) {
                              return (
                                <Typography
                                  variant="body2"
                                  component="div"
                                  color="text.secondary"
                                >
                                  {getAttendeeAccessDisplay(
                                    attendee,
                                    currentClass,
                                    intl.formatMessage,
                                  )}
                                </Typography>
                              );
                            }
                            return null;
                          })()}
                        </>
                      }
                    />
                    <AttendeeImportantInfoButton attendee={attendee} />
                  </Box>
                </ListItem>
              ))}
            </List>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
